import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, LogIn, Mail, RefreshCcw, LogOut } from 'lucide-react';
import AuthLayout from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { forgotPassword } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { roleHome } from '@/lib/auth';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signOut, refreshProfile, user, profile, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);

  // If the axios interceptor bounced the user here due to a 401, surface a
  // one-shot toast. The flag is set in lib/api.js and cleared here.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem('lms_session_expired') === '1') {
        window.sessionStorage.removeItem('lms_session_expired');
        toast.info('Session expired, please sign in again.');
      }
    } catch { /* private mode / blocked storage */ }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasPortalParams = params.has('email') || params.has('role');

    if (!hasPortalParams) return;

    const portalEmail = params.get('email');
    if (portalEmail) {
      setEmail(portalEmail);
    }

    params.delete('email');
    params.delete('role');

    const nextSearch = params.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  }, [location.hash, location.pathname, location.search]);

  // If already signed in, bounce to the right dashboard.
  if (!loading && user && profile) {
    const dest = location.state?.from || roleHome(profile) || '/student/dashboard';
    return <Navigate to={dest} replace />;
  }

  const handleRecoverProfile = async () => {
    try {
      setRecovering(true);
      const profileRow = await refreshProfile();
      if (profileRow?.role) {
        toast.success('Workspace restored');
        navigate(location.state?.from || roleHome(profileRow) || '/student/dashboard', {
          replace: true,
        });
        return;
      }
      toast.error("We still couldn't load your role. Try signing out and in again.");
    } catch (err) {
      toast.error(err?.message || 'Failed to reload your workspace');
    } finally {
      setRecovering(false);
    }
  };

  const handleSessionReset = async () => {
    try {
      setRecovering(true);
      await signOut();
      toast.success('Signed out');
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err?.message || 'Failed to sign out');
    } finally {
      setRecovering(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter your email and password');
      return;
    }
    try {
      setSubmitting(true);
      const { user: signedInUser } = await signIn({ email, password });
      toast.success('Welcome back');
      navigate(location.state?.from || roleHome(signedInUser) || '/student/dashboard', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail || err?.message || 'Failed to sign in';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!loading && user && !profile) {
    return (
      <AuthLayout subtitle="Session recovery" title="We couldn't load your workspace">
        <div className="space-y-5" data-testid="login-recovery">
          <p className="text-sm text-[#062B1A]/70 leading-relaxed">
            Your account is signed in, but the app could not read your role and profile yet.
            This can happen after an interrupted sign-in or a stale session.
          </p>
          <div className="rounded-xl border border-[rgba(6, 43, 26,0.08)] bg-[#F7F8FA] p-4 text-sm text-[#062B1A]/70">
            <p className="font-medium text-[#062B1A]">Next step</p>
            <p className="mt-1">
              Try reloading your workspace first. If that still fails, sign out and log in again.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Button
              type="button"
              onClick={handleRecoverProfile}
              disabled={recovering}
              className="h-11 bg-[#D4AF37] text-[#062B1A] hover:bg-[#C9A431] font-semibold"
            >
              {recovering ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCcw className="w-4 h-4 mr-2" />
              )}
              Retry workspace
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSessionReset}
              disabled={recovering}
              className="h-11 border-[rgba(6, 43, 26,0.16)] text-[#062B1A]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  const handleForgotPassword = async () => {
    const target = (forgotEmail || '').trim();
    if (!target) { toast.error('Enter the email you sign in with'); return; }
    try {
      setForgotSending(true);
      const res = await forgotPassword(target);
      toast.success(res.data?.message || 'If that email has an account, a reset link is on its way.');
      setForgotOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset link could not be sent.');
    } finally { setForgotSending(false); }
  };

  return (
    <>
    <AuthLayout
      subtitle="Sign in to your account"
      title="Welcome back"
      footer={
        <span>
          New student?{' '}
          <Link
            to="/signup"
            data-testid="login-signup-link"
            className="text-[#062B1A] font-semibold hover:text-[#D4AF37] transition-colors"
          >
            Create an account
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-[#062B1A]">
            Email address
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="login-email-input"
            className="h-11 border-[rgba(6, 43, 26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212, 175, 55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-sm font-medium text-[#062B1A]">
              Password
            </Label>
            <button
              type="button"
              className="text-xs font-medium text-[#062B1A]/60 hover:text-[#D4AF37] transition-colors"
              onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
              data-testid="login-forgot-password"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              data-testid="login-password-input"
              className="h-11 pr-10 border-[rgba(6, 43, 26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212, 175, 55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#062B1A]/40 hover:text-[#062B1A] transition-colors"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="login-submit-button"
          className="w-full h-11 bg-[#D4AF37] text-[#062B1A] hover:bg-[#C9A431] focus-visible:ring-4 focus-visible:ring-[rgba(212, 175, 55,0.45)] font-semibold"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Signing in…
            </>
          ) : (
            <>
              <LogIn className="w-4 h-4 mr-2" />
              Sign in
            </>
          )}
        </Button>

        <div className="pt-4 border-t border-[rgba(6, 43, 26,0.08)]">
          <p className="text-xs text-[#062B1A]/50 leading-relaxed">
            Use the same login for{' '}
            <span className="font-semibold text-[#062B1A]/70">students</span>,{' '}
            <span className="font-semibold text-[#062B1A]/70">instructors</span>, and{' '}
            <span className="font-semibold text-[#062B1A]/70">admins</span>. You'll be routed
            to the right workspace automatically.
          </p>
        </div>
      </form>
    </AuthLayout>

    <Dialog open={forgotOpen} onOpenChange={(open) => { if (!forgotSending) setForgotOpen(open); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Reset your password</DialogTitle></DialogHeader>
        <p className="text-sm text-[#062B1A]/70">
          Enter the email you sign in with. If it has an account, we'll send a link to choose a new
          password. The link works once and expires in an hour.
        </p>
        <div className="space-y-2">
          <Label htmlFor="forgot-email" className="text-sm font-medium text-[#062B1A]">Email</Label>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="forgot-password-email"
            className="h-11"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setForgotOpen(false)} disabled={forgotSending}>Cancel</Button>
          <Button onClick={handleForgotPassword} disabled={forgotSending} data-testid="forgot-password-submit" className="bg-[#062B1A] text-white">
            {forgotSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
            Send reset link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
